import { css } from '@emotion/react';
import MuiLink from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import { Theme } from '@mui/material/styles';

import CopyToClipboardButton from 'components/CopyToClipboardButton/CopyToClipboardButton';
import TaskStateBar from 'components/TaskStateBar/TaskStateBar';
import WorkflowUserName from 'components/WorkflowUserName/WorkflowUserName';
import RunStateLabel from 'pages/WorkflowRunsPage/Runs/RunStateLabel';

import formatWorkflowUser from 'utils/formatWorkflowUser';
import { formatDate } from 'utils/formatDate';
import { taskStateColor } from 'utils/taskStateColors';

import IAccount from 'types/IAccount';
import IRunEntry from 'types/IRunEntry';
import { isAggregateRunState } from 'types/RunState';
import IColumnDefinition from 'pages/WorkflowRunsPage/Table/IColumnDefinition';

export function dateColumn(): IColumnDefinition<IRunEntry> {
  return {
    header: 'Date',
    key: 'updated_utc',
    render: (run) => formatDate(run.updated_utc),
  };
}

export function idColumn(): IColumnDefinition<IRunEntry> {
  return {
    header: 'Id',
    key: 'id',
    render: (run) => (
      <span
        css={css`
          display: inline-flex;
          align-items: center;
        `}
        onClick={(event) => event.stopPropagation()}
      >
        {run.id}
        <CopyToClipboardButton data={run.id} size="small" subject="workflow ID" />
      </span>
    ),
  };
}

export function userColumn(
  usersById: Map<string | null | undefined, { full_name?: string; username?: string }>,
  accountsById: Map<string, IAccount>,
): IColumnDefinition<IRunEntry> {
  return {
    header: 'User',
    key: 'user_id',
    render: (run) => {
      const { name, isAccountWorker } = formatWorkflowUser(
        run.user_id,
        usersById.get(run.user_id),
        accountsById.get(run.user_id),
      );
      return <WorkflowUserName name={name} isAccountWorker={isAccountWorker} />;
    },
  };
}

export function stateColumn(
  theme: Theme,
  options?: { onStateClick?: (state: string) => void; selectedState?: string },
): IColumnDefinition<IRunEntry> {
  const { onStateClick, selectedState } = options ?? {};
  return {
    header: 'State',
    key: 'state',
    render: (run) => {
      const selected = selectedState === run.state;
      const color = taskStateColor(run.state);
      const label = <RunStateLabel state={run.state} />;
      return (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 6px;
          `}
        >
          {onStateClick && isAggregateRunState(run.state) ? (
            <button
              type="button"
              aria-pressed={selected}
              aria-label={`Filter workflows by ${run.state}`}
              title={selected ? `Clear ${run.state} filter` : `Filter workflows by ${run.state}`}
              onClick={(event) => {
                event.stopPropagation();
                onStateClick(run.state);
              }}
              css={css`
                appearance: none;
                margin: 0;
                padding: 0 4px;
                border: none;
                border-radius: 4px;
                background: ${selected ? `color-mix(in srgb, ${color} 22%, transparent)` : 'transparent'};
                box-shadow: ${selected ? `inset 0 0 0 1px ${color}` : 'none'};
                color: inherit;
                font: inherit;
                text-decoration: none;
                cursor: pointer;

                &:hover {
                  background: color-mix(in srgb, ${color} 16%, transparent);
                }
              `}
            >
              {label}
            </button>
          ) : (
            label
          )}
          {run.has_worker_lost_tasks && (
            <Tooltip title="This workflow has tasks whose worker was lost">
              <span
                css={css`
                  color: ${theme.palette.warning.main};
                  font-size: 0.75rem;
                  font-weight: 600;
                `}
              >
                WORKER LOST
              </span>
            </Tooltip>
          )}
        </div>
      );
    },
  };
}

function taskCount(run: IRunEntry): number {
  return Object.values(run.task_state_count ?? {}).reduce((sum, n) => sum + n, 0);
}

export function tasksColumn(options?: {
  onStateClick?: (state: string) => void;
  selectedState?: string;
}): IColumnDefinition<IRunEntry> {
  return {
    header: 'Tasks',
    key: 'task_state_count',
    render: (run) => (
      <TaskStateBar
        counts={run.task_state_count ?? {}}
        onStateClick={options?.onStateClick}
        selectedState={options?.selectedState}
      />
    ),
  };
}

export function taskCountColumn(): IColumnDefinition<IRunEntry> {
  return {
    header: 'Count',
    // @ts-expect-error – display-only column
    key: 'task_count',
    render: (run) => (
      <span
        css={css`
          font-variant-numeric: tabular-nums;
        `}
      >
        {taskCount(run)}
      </span>
    ),
  };
}

export function errorsColumn(onShowErrors: (run: IRunEntry) => void): IColumnDefinition<IRunEntry> {
  return {
    header: '',
    // @ts-expect-error – Never mind that there is no "errors" property on IRunEntry
    key: 'errors',
    render: (run) =>
      (run.task_state_count?.FAILED ?? 0) > 0 && (
        <MuiLink
          component="button"
          onClick={(event) => {
            event.stopPropagation();
            onShowErrors(run);
          }}
        >
          Errors
        </MuiLink>
      ),
  };
}
